import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DetailField,
  Loader,
  ModalCreatedFooter,
  VehicleDetailField,
} from "@/src/modules/core/components";
import {resolveTimezone} from "@/src/modules/core/lib";
import {getCreatorDisplayName} from "@/src/modules/core/lib/get-creator-display-name";
import {useStoredCompany} from "@/src/modules/company/store";
import {usePropertyTypes} from "@/src/modules/properties/hooks/use-property-types";
import {formatUsPhoneDisplay} from "@/src/modules/properties/lib/us-phone";
import {HouseholdAmenityRestrictionBanner} from "@/src/modules/households/components/household-amenity-restriction-banner";
import {isAmenityKeyRestricted} from "@/src/modules/households/lib/household-amenity-restriction";

import {SafelistingCoverageSummary} from "./safelisting-coverage-summary";
import {SafelistingCoverageStatusCluster} from "./safelisting-coverage-status-cluster";
import {SafelistingDaysStatusList} from "./safelisting-days-status-list";
import useSafelisting from "../hooks/use-safelisting";
import useSafelistingRangeHours from "../hooks/use-safelisting-range-hours";
import {
  computeSafelistingCoverageFromDays,
  getSafelistingCoverageDateWord,
} from "../lib/compute-safelisting-coverage-from-days";
import {
  hasCreateTimeLimitOverride,
  isSafelistingExcludedFromQuota,
  shouldShowAccountabilityAdjustedHint,
} from "../lib/safelisting-quota-display";
import {SAFELISTING_OVERRIDE_READ_LINES} from "../lib/safelisting-override-help";

function LongBlock({title, body}: {title: string; body: string | null | undefined}) {
  const t = body?.trim();
  return (
    <DetailField label={title} className="sm:col-span-2">
      {t ? (
        <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words">{t}</pre>
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </DetailField>
  );
}

type Props = {
  open: boolean;
  safelistingId: number | null;
  onOpenChange: (open: boolean) => void;
  /** When false, private notes omitted (e.g. residents). */
  showPrivateNotes: boolean;
};

export function SafelistingDetailModal({
  open,
  safelistingId,
  onOpenChange,
  showPrivateNotes,
}: Props) {
  const company = useStoredCompany();
  const timeZone = resolveTimezone(company?.timezone);
  const {propertyTypes} = usePropertyTypes();
  const rangeHours = useSafelistingRangeHours();
  const enabledId = open && safelistingId ? safelistingId : null;
  const {safelisting, isPending} = useSafelisting(enabledId);
  const coverage =
    safelisting != null && rangeHours
      ? computeSafelistingCoverageFromDays(
          safelisting.days,
          timeZone,
          getSafelistingCoverageDateWord(safelisting.property, propertyTypes),
          rangeHours,
        )
      : null;
  const excludedFromQuota = isSafelistingExcludedFromQuota(safelisting ?? {});
  const showAdjustedHint =
    safelisting != null && shouldShowAccountabilityAdjustedHint(safelisting, timeZone);
  const showCreateOverride =
    safelisting != null && hasCreateTimeLimitOverride(safelisting);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] gap-0 overflow-y-auto p-6 sm:max-w-[700px]">
        {isPending || !safelisting ? (
          <DialogHeader>
            <DialogTitle className="sr-only">Loading safelisting</DialogTitle>
            <Loader className="h-[10rem]" />
          </DialogHeader>
        ) : (
          <div className="flex flex-col gap-4">
            <DialogHeader className="gap-1 p-0">
              <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
                Safelisting #{safelisting.safelisting_id}
                <SafelistingCoverageStatusCluster
                  safelisting={safelisting}
                  timeZone={timeZone}
                  startHumanLabel={coverage?.startHumanLabel}
                  badgeClassName="text-xs"
                />
              </DialogTitle>
            </DialogHeader>

            {isAmenityKeyRestricted(safelisting.household, "safelisting") ? (
              <HouseholdAmenityRestrictionBanner />
            ) : null}

            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
              <VehicleDetailField
                vehicle={safelisting.vehicle}
                fallbackId={safelisting.vehicle_id}
              />
              <DetailField label="Created by">
                {getCreatorDisplayName(safelisting.created_by)}
              </DetailField>
              <DetailField label="Household">
                {safelisting.household?.name?.trim() ?? "—"}
              </DetailField>
              <DetailField label="Resident">
                {safelisting.resident?.name?.trim() ?? "—"}
              </DetailField>
              <DetailField label="Recipient name">
                {safelisting.recipient_name?.trim() ?? "—"}
              </DetailField>
              <DetailField label="Recipient email">
                {safelisting.recipient_email?.trim() ?? "—"}
              </DetailField>
              <DetailField label="Recipient phone">
                {formatUsPhoneDisplay(safelisting.recipient_phone)}
              </DetailField>
            </div>

            {coverage ? <SafelistingCoverageSummary coverage={coverage} /> : null}

            <SafelistingDaysStatusList
              days={safelisting.days}
              timeZone={timeZone}
              excludedFromQuota={excludedFromQuota}
              accountabilityAdjusted={showAdjustedHint}
            />

            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
              {showPrivateNotes ? (
                <>
                  <LongBlock title="Notes" body={safelisting.notes} />
                  <LongBlock title="Private notes" body={safelisting.private_notes} />
                </>
              ) : null}
              {showCreateOverride ? (
                <DetailField label="Limit override (at create)" className="sm:col-span-2">
                  {safelisting.override_reason?.trim() ? (
                    <p className="whitespace-pre-wrap break-words">{safelisting.override_reason}</p>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                  {(safelisting.override_limit_days ?? 0) > 0 ? (
                    <p className="text-muted-foreground mt-2 text-xs">
                      {SAFELISTING_OVERRIDE_READ_LINES.limitDays(safelisting.override_limit_days ?? 0)}
                    </p>
                  ) : null}
                  {safelisting.override_days_in_a_row === 1 ? (
                    <p className="text-muted-foreground mt-2 text-xs">
                      {SAFELISTING_OVERRIDE_READ_LINES.daysInARow}
                    </p>
                  ) : null}
                </DetailField>
              ) : null}
            </div>

            <ModalCreatedFooter createdAt={safelisting.created_at} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
